// Pure helpers for the bank screens — kept UI-free so Vitest can pin the live-parity
// behaviours (money formatting, the post gate, tax preview, rule-type cycling).

/** Money with the ACCOUNT's currency symbol (R / $ / € — per-bank-account currency).
 * signed=false → magnitude only (the colour class carries direction). No symbol given → the
 * company's base currency; never a literal R (owner 2026-10-19: Finnish / Australian companies
 * were shown rand). Before the company has loaded there is no symbol: the bare figure shows. */
export function fmtMoney(value, symbol, signed){
  const sym = symbol || _baseSym;
  return (sym ? sym + ' ' : '') + fmtAmountOnly(value, signed);
}

/** The figure alone, 1,234.56 — for a box that already carries its currency label. */
export function fmtAmountOnly(value, signed){
  const n = Number(value || 0);
  const v = signed ? n : Math.abs(n);
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// App-wide base-currency symbol = the COMPANY's base currency. Set once per company load
// (tshell reads it off /me → base_currency_symbol) so every base-currency figure — Dashboard,
// Journals, Contacts, Expenses, Recurring — follows the company instead of a hardcoded R. Bank and
// document screens override with a per-ACCOUNT / per-CONTACT symbol via fmtMoney directly. Empty
// until the company loads, and cleared when another company opens, so a figure never shows another
// company's currency (Callan 2026-09-14: "the app must align with the company's currency").
let _baseSym = '';
export function setBaseCurrencySymbol(sym){ _baseSym = sym ? String(sym) : ''; }
export function baseCurrencySymbol(){ return _baseSym; }
// …and its ISO code and id (/me base_currency_code / base_currency_id), so a screen can tell a foreign
// account or document from a base one without assuming the rand. Set and cleared with the symbol (tshell).
let _baseCode = '', _baseId = null;
export function setBaseCurrencyCode(code){ _baseCode = code ? String(code).toUpperCase() : ''; }
export function baseCurrencyCode(){ return _baseCode; }
export function setBaseCurrencyId(id){ _baseId = (id === null || id === undefined || id === '') ? null : String(id); }
/** The company base as far as it is known: { id, code, symbol }. */
export function baseCurrency(){ return { id: _baseId, code: _baseCode, symbol: _baseSym }; }

/** <base> 1,234.56 — magnitude only; the colour class carries direction (live line amounts). */
export function fmtR(value){
  return fmtMoney(value, _baseSym, false);
}

/** <base> -29,825.35 — SIGNED (live ledger/projected/running-balance figures keep the minus). */
export function fmtRS(value){
  return fmtMoney(value, _baseSym, true);
}

/** <base> -29,412 — SIGNED, WHOLE rand (no decimals). The dashboard KPI tiles use this so large
 * figures (e.g. R 16,970,136.94) don't overflow the tile (owner 2026-09-23: "remove decimals from
 * the dashboard figures — prevent overflow"). Rounds to the nearest rand. */
export function fmtR0(value){
  const n = Math.round(Number(value || 0));
  // -0 → 0 so a rounded-down tiny negative never shows "R -0"
  return (_baseSym ? _baseSym + ' ' : '') + (n === 0 ? 0 : n).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** Signed colour class for a line amount (deposits green, payments red — live). */
export function amountClass(amount){
  return Number(amount) < 0 ? 'amt-out' : 'amt-in';
}

/** Sign tone for a TOTAL / net / balance figure (owner 2026-08-26, app-wide — TOTALS ONLY, never
 * line items): green when positive, red when negative, neutral at zero. Add the returned class to
 * the total's element. Deliberately NOT applied to totals whose colour already carries a different
 * meaning (money in/out direction, out-of-balance difference, VAT payable, overdue aging). */
export function totalTone(v){
  const n = Number(v) || 0;
  return n > 0 ? 'tot-pos' : n < 0 ? 'tot-neg' : '';
}

/** Projected balance of ONE journal leg's account AFTER this entry posts, in the account's
 * NATURAL terms (Andre 2026-09-17: "if I post this, this account's new balance is R…"). A
 * debit-normal account (assets/expenses) shows a debit balance as positive; a credit-normal
 * account (liabilities/income/equity) shows a credit balance as positive.
 *   current    the account's CURRENT posted balance in the ledger's debit-positive convention
 *              (ref.balances[accountId] / a contact's balance) — Σ signed journal_detail.amount,
 *              debits +, credits -.
 *   legAmount  this leg's SIGNED ledger delta as stored (debit leg +, credit leg -). Using the
 *              leg's own amount (not the entry gross) keeps the debit side exact even with VAT.
 *   normalSide the account's normal balance side: 'debit' | 'credit' (account.normal_side).
 * The sign rule (a debit increases assets/expenses and decreases liabilities/income/equity; a
 * credit is the opposite) falls straight out: raw = current + legAmount is the ledger projection,
 * then a credit-normal account is flipped so its natural balance reads positive. Returns the
 * SIGNED natural projected balance (an abnormal balance keeps its minus). */
export function projectedBalance(current, legAmount, normalSide){
  const raw = Number(current || 0) + Number(legAmount || 0);   // ledger (debit-positive) projection
  const factor = normalSide === 'credit' ? -1 : 1;             // natural terms flip credit-normal accts
  const v = Math.round(factor * raw * 100) / 100;
  return v === 0 ? 0 : v;                                       // never hand back -0
}

/** Ledger-code display. ALL codes are stored FLAT 4-alphanumeric (owner rule 2026-08-23:
 * admin Countries → Ledger codes, no sub-accounts) and every bank GL shares the one general
 * bank code (9500). Nothing dotted should exist any more; a stray decorative '.000' base is
 * still collapsed defensively, anything else passes through verbatim so bad data stays visible. */
export function formatGlCode(code){
  const s = String(code == null ? '' : code).trim();
  if(!s) return '';
  const m = s.match(/^(\d{3,4})\.(\d{3})$/);
  if(!m) return s;                               // not NNNN.NNN → verbatim
  return m[2] === '000' ? m[1] : s;              // drop a decorative .000 base, keep a real .NNN sub
}

/** The live Post-to-Ledger gate: null = may post, else the exact live refusal text. */
export function postGateMessage(unallocated, allocated){
  if(Number(unallocated) > 0) return `${unallocated} entries still need GL allocation`;
  if(Number(allocated) <= 0) return 'No entries to post';
  return null;
}

/** Live tax-INCLUSIVE preview: portion of amount at rate% (amount × r ÷ (100 + r)). */
export function inclusiveTax(amount, rate){
  const r = Number(rate || 0);
  if(!r) return 0;
  return Math.round((Number(amount || 0) * r / (100 + r)) * 100) / 100;
}

/** The live rules screen cycles the match type on click: exact → starts_with → fuzzy → exact. */
export function cycleMatchType(current){
  // live rule kinds incl. 'nl' natural-language rules (LLM-context only, never pattern-matched)
  return { exact: 'starts_with', starts_with: 'fuzzy', fuzzy: 'nl', nl: 'exact' }[current] || 'exact';
}

export const MATCH_TYPE_LABELS = { exact: 'Exact', starts_with: 'Starts with', fuzzy: 'Fuzzy',
                                   nl: 'Natural language (AI)' };

/** Status chip label for a staged line. */
export function lineStatusLabel(line){
  if(line.is_duplicate && line.status !== 'posted') return 'Possible duplicate';
  return { unallocated: 'Unallocated', allocated: 'Allocated', posted: 'Posted', ignored: 'Ignored' }[line.status] || line.status;
}

/** Live incrementRef, verbatim: 'BS1'→'BS2', 'INV009'→'INV010' (padding kept); no trailing
 * number → unchanged. */
export function incrementRef(ref){
  if(!ref) return '';
  const match = String(ref).match(/^(.*?)(\d+)$/);
  if(match){
    const num = parseInt(match[2], 10) + 1;
    return match[1] + String(num).padStart(match[2].length, '0');
  }
  return ref;
}

/** Auto-mode running balance: staged lines in date order, cumulative from the posted
 * ledger balance (live's bank-feed BALANCE column). Returns [{line, balance}]. */
export function runningBalances(lines, ledgerBalance){
  const ordered = [...lines].sort((a, b) =>
    a.txn_date === b.txn_date ? (a.id - b.id) : (a.txn_date < b.txn_date ? -1 : 1));
  let bal = Number(ledgerBalance || 0);
  return ordered.map(line => { bal += Number(line.amount || 0); return { line, balance: bal }; });
}

/** Import-REVIEW running balance: the imported statement as ONE continuous list — no
 * Deposits/Payments split (Andre 2026-09-14: "Auto mode is a continuous bank statement").
 * Rows are ordered chronologically (stable by ORIGINAL index, so same-day statement order is
 * kept) and the balance accumulates from `openingBase` — the statement's stated opening, so the
 * last row reconciles to the printed closing (a faithful replica of the statement's Balance
 * column). Each row keeps its original index `i`, because selection/flip/remove address the
 * txn in the UNSORTED preview array. Optional `inScope(tx)` limits to one account of a
 * multi-account import. Returns [{ t, i, running }]. */
export function previewRunning(txns, openingBase, inScope){
  const rows = [];
  (txns || []).forEach((t, i) => { if(!inScope || inScope(t)) rows.push({ t, i }); });
  rows.sort((a, b) => {
    const da = String((a.t || {}).date || ''), db = String((b.t || {}).date || '');
    return da === db ? (a.i - b.i) : (da < db ? -1 : 1);
  });
  let bal = Number(openingBase || 0);
  for(const r of rows){
    bal = Math.round((bal + (Number((r.t || {}).amount) || 0)) * 100) / 100;
    r.running = bal;
  }
  return rows;
}

/** Live scoreGL keyword hints, verbatim — description keyword → GL-name keyword. */
export const GL_HINTS = {
  'CHARGE': ['bank', 'charge', 'fee', 'admin'],
  'INTEREST': ['interest'],
  'SALARY': ['salary', 'wage', 'payroll'],
  'INSURANCE': ['insurance', 'premium', 'brightrock', 'sanlam', 'old mutual', 'discovery', 'outsurance'],
  'RENT': ['rent', 'lease', 'property'],
  'ELECTRIC': ['electric', 'eskom', 'municipal', 'water', 'rates'],
  'FUEL': ['fuel', 'petrol', 'diesel', 'engen', 'shell', 'sasol', 'caltex', 'total'],
  'TELKOM': ['telkom', 'vodacom', 'mtn', 'cell', 'fibre', 'afrihost'],
  'SUBSCRI': ['subscri', 'netflix', 'spotify', 'microsoft', 'google', 'adobe'],
  'SHOP': ['shoprite', 'checkers', 'woolworth', 'pick', 'spar', 'makro'],
  'BOND': ['bond', 'homeloan', 'home loan', 'mortgage', 'sbsa'],
  'PROFESS': ['audit', 'legal', 'consult', 'account', 'attorney'],
  'REPAIR': ['repair', 'maintenance', 'service'],
  'TRAVEL': ['travel', 'flight', 'uber', 'bolt', 'car hire'],
  'ADVERTI': ['adverti', 'marketing', 'google ads', 'facebook'],
  'STATION': ['stationery', 'office', 'supplies'],
  'RECEIV': ['receiv', 'income', 'deposit', 'payment received']
};

/** Confidence label for the AI suggestion chip (live shows "AI 67%"): a bounded,
 * monotonic mapping from the scoreGL score. */
export function aiPct(score){
  return Math.min(97, 35 + Number(score || 0) * 4);
}

/** Live scoreGL, ported: rank candidate GL accounts for a line description.
 * `usage` (optional) is the reference bundle's gl_usage map — live's popularity
 * tier adds min(posted-count, 10) so frequently-used accounts surface even with
 * generic names. */
export function scoreGL(description, accounts, usage){
  const desc = String(description || '').toUpperCase();
  const words = desc.split(/[\s\-\/\*]+/).filter(w => w.length >= 3);
  const scored = [];
  for(const account of accounts){
    const name = String(account.name || '').toUpperCase();
    let score = 0;
    if(desc.indexOf(name) >= 0) score += 10;
    if(name && name.indexOf(desc) >= 0) score += 8;
    for(const w of words) if(name.indexOf(w) >= 0) score += 3;
    for(const hint in GL_HINTS){
      if(name.indexOf(hint) >= 0 && GL_HINTS[hint].some(h => desc.indexOf(h.toUpperCase()) >= 0)) score += 5;
    }
    if(usage) score += Math.min(Number(usage[String(account.id)] || 0), 10);
    if(score > 0) scored.push({ account, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored;
}

/** Live [D]/[C]/[S]/[V] tag on control-account suggestions in the alloc popover. */
export function controlTag(account){
  return { debtors: 'D', creditors: 'C', stock: 'S', vat: 'V' }[(account || {}).control_type] || '';
}

/** Live preview match-tier badge: every allocated row names its provenance.
 * Unmatched / pending-AI rows return null — they carry their own affordances. */
export function matchBadge(txn){
  const mt = (txn || {}).match_type;
  if(!mt || mt === 'none' || mt === 'ai_suggest') return null;
  if(mt === 'ai') return { label: `AI ${confPct(txn.ai_confidence)}%`, cls: 'cb-badge-ai' };
  const labels = { contact: 'contact', exact: 'exact', starts_with: 'starts with',
                   fuzzy: 'fuzzy', manual: 'manual' };
  return { label: labels[mt] || mt, cls: 'cb-badge-' + mt };
}

/** Live D/P table footers: deposits and payments totalled separately (magnitudes). */
export function previewTotals(txns){
  let inTotal = 0, outTotal = 0;
  for(const t of txns || []){
    const amount = Number(t.amount) || 0;
    if(amount >= 0) inTotal += amount; else outTotal += -amount;
  }
  return { inTotal: Math.round(inTotal * 100) / 100, outTotal: Math.round(outTotal * 100) / 100 };
}

/** Live closing-balance reconciliation: Opening + Σ movements vs the statement's
 * stated closing — the arithmetic proof the statement was read correctly.
 * Returns null unless BOTH balances are stated (live hides the check). */
export function closingCheck(header, txns){
  const opening = Number((header || {}).opening_balance);
  const stated = Number((header || {}).closing_balance);
  if(!Number.isFinite(opening) || !Number.isFinite(stated)
     || (header || {}).opening_balance === null || (header || {}).closing_balance === null
     || (header || {}).opening_balance === undefined || (header || {}).closing_balance === undefined)
    return null;
  let expected = opening;
  for(const t of txns || []) expected += Number(t.amount) || 0;
  expected = Math.round(expected * 100) / 100;
  const delta = Math.round(Math.abs(stated - expected) * 100) / 100;
  return { expected, stated, delta, ok: delta <= 0.01 };
}

/** Real AI confidence (0-1) -> the chip's percent label; falls back to the heuristic. */
export function confPct(confidence, fallbackScore){
  const c = Number(confidence);
  if(c > 0 && c <= 1) return Math.round(c * 100);
  return aiPct(Number(fallbackScore || 0));
}

/** List/table dates in the COMPANY's date format (live dateFmt: dd/mm/yyyy,
 * mm/dd/yyyy or yyyy-mm-dd, dash variants included, dd.mm.yyyy for Finland; ZA dd/mm/yyyy default). */
export function formatDate(iso, dateFormat){
  const s = String(iso || '').slice(0, 10);
  if(!/^\d{4}-\d{2}-\d{2}$/.test(s)) return iso || '';
  const [y, m, d] = s.split('-');
  const f = String(dateFormat || '').toLowerCase().trim();
  const sep = f.indexOf('-') !== -1 ? '-' : (f.indexOf('.') !== -1 ? '.' : '/');
  if(f.startsWith('yyyy')) return [y, m, d].join(sep);
  if(f.startsWith('mm')) return [m, d, y].join(sep);
  return [d, m, y].join(sep);
}

/** Import-preview tab counts (live results header: matched / unmatched / duplicates). */
export function previewCounts(txns){
  const counts = { deposits: 0, payments: 0, matched: 0, unmatched: 0, dupes: 0, suggested: 0 };
  for(const t of txns || []){
    if(Number(t.amount) >= 0) counts.deposits++; else counts.payments++;
    if(t.is_duplicate) counts.dupes++;
    const mt = t.match_type || 'none';
    if(mt === 'ai_suggest'){ counts.suggested++; counts.unmatched++; }
    else if(mt === 'none') counts.unmatched++;
    else counts.matched++;
  }
  return counts;
}

// Turn a finished import job's per-file errors into ONE persistent notice for the cashbook —
// so a failed extraction is a lasting message with a fix, not a 2.6s toast that vanishes while
// the user stares at a 0-row review. `needsKey` flags the "no AI key" case (there is no platform
// fallback key — users bring their own), letting the UI link to Settings ▸ API Keys. null = no error.
export function importErrorNotice(files){
  const errs = (files || []).filter(f => f && f.error);
  if(!errs.length) return null;
  const needsKey = errs.some(f => /api key|not configured/i.test(String(f.error || '')));
  const msg = needsKey
    ? 'To import bank statements, add your own AI API key.'
    : errs.map(f => `${f.filename || 'statement'}: ${f.error}`).join(' · ');
  return { needsKey, msg };
}

// GL/account typeahead filter (the float-dropdown convention): match code OR name,
// cap at 12 rows, blank query shows nothing.
export function filterGlAccounts(accounts, q){
  q = String(q || '').trim().toLowerCase();
  if(!q) return [];
  return (accounts || []).filter(a => String(a.code).toLowerCase().includes(q) ||
    String(a.name || '').toLowerCase().includes(q)).slice(0, 12);
}

/** The one-click suggestion for an unallocated line: the ALLOCATION LEARNER's (API `line.learned` —
 * learned from how this company actually booked the same merchant + branch) when it has one, else the
 * name-based `scoreGL` guess (`fallbackTop`). → { account_id, code, name, pct, learned, evidence,
 * reason } or null. Contact (debtor/creditor) suggestions keep the existing flow for now. */
export function suggestionFor(line, fallbackTop){
  const l = line && line.learned;
  if(l && l.account_id && !l.contact_id){
    // kind 'rule': a rule the line only loosely matches (one shared word) — offered, never booked
    const rule = l.kind === 'rule';
    return { account_id: l.account_id, code: l.code || '', name: l.name || '', learned: true, rule,
             pct: Math.round(Number(l.confidence || 0) * 100),
             evidence: rule ? '' : `${l.count}/${l.total}`, reason: l.reason || '' };
  }
  if(fallbackTop && fallbackTop.account){
    return { account_id: fallbackTop.account.id, code: fallbackTop.account.code || '',
             name: fallbackTop.account.name || '', learned: false, pct: null, evidence: '', reason: '' };
  }
  return null;
}

// ── one currency per figure (owner 2026-10-19: "I DO NOT WANT CURRENCIES TO BLEED INTO EACH OTHER") ──
// Every figure shows the currency it is in, with that currency's mark; nothing assumes the rand; no
// figure or total adds two currencies.

const _cents = v => Math.round(Number(v || 0) * 100) / 100;

// true / false, or null when it cannot be told (the account names a currency, the base is not known)
function _foreign(acct, base){
  const a = acct || {}, b = base || {};
  if(typeof a.is_foreign === 'boolean') return a.is_foreign;
  const hasId = a.currency_id !== null && a.currency_id !== undefined && a.currency_id !== '';
  const code = a.own_currency_code || a.currency_code;
  if(!hasId && !code) return false;                // no currency of its own → the company base (the API's rule)
  if(hasId && b.id !== null && b.id !== undefined && b.id !== '') return Number(a.currency_id) !== Number(b.id);
  if(code && b.code) return String(code).toUpperCase() !== String(b.code).toUpperCase();
  return null;
}

/** Is this bank account in a currency other than the company base? The API says so (`is_foreign` on
 * the balance block). An older API does not: compare the account's currency with the company base,
 * by id (`base.id` = bank-reference base_currency_id) or by code (`base.code`) — never with 'ZAR'.
 * An account with no currency is in the base; when the base is not known → false. */
export function isForeignAccount(acct, base){ return _foreign(acct, base) === true; }

/** A bank account's own figures and the ONE currency they are in. In a balance block (a card from
 * GET …/bank-accounts, or the per-account /balance) `balance`, `staged_in`, `staged_out` and
 * `projected` are BASE — ledger sums; Total Cash adds them. `own_*` are the same figures in the
 * account's own currency (equal to the base ones on a base account). `statement_balance`, line
 * amounts and running balances are always the account's own currency.
 *   block  the balance block;  acct  the card (currency_id / _code / _symbol), defaults to block;
 *   base   { id, code } of the company base currency (see isForeignAccount).
 * → { own, foreign, code, symbol, balance, staged_in, staged_out, projected }
 *   own=true:  the figures are in the account's currency and `symbol` is its mark.
 *   own=false: an older API (no own_*) on a FOREIGN account, or one whose currency cannot be told
 *              from the base — the BASE figures with the BASE mark (never the account's mark on a
 *              base figure); they cannot be set against the statement. */
export function bankFigures(block, acct, base){
  const b = block || {}, a = acct || b;
  const f = typeof b.is_foreign === 'boolean' ? b.is_foreign : _foreign(a, base);
  const code = b.own_currency_code || a.own_currency_code || a.currency_code || '';
  if(b.own_balance !== undefined && b.own_balance !== null){
    const foreign = f === true;
    const sIn = _cents(b.own_staged_in), sOut = _cents(b.own_staged_out), bal = _cents(b.own_balance);
    return { own: true, foreign, code,
             // a foreign currency without a symbol shows its code — never the base mark
             symbol: b.own_currency_symbol || a.currency_symbol || (foreign ? code : _baseSym),
             balance: bal, staged_in: sIn, staged_out: sOut,
             projected: (b.own_projected !== undefined && b.own_projected !== null)
               ? _cents(b.own_projected) : _cents(bal + sIn - sOut) };
  }
  const baseFigs = { balance: _cents(b.balance), staged_in: _cents(b.staged_in), staged_out: _cents(b.staged_out),
                     projected: _cents(b.projected) };
  // a base-currency account: its base figures ARE its own currency (the rand company's cards unchanged)
  if(f === false) return { own: true, foreign: false, code, symbol: a.currency_symbol || _baseSym, ...baseFigs };
  return { own: false, foreign: f === true, code, symbol: _baseSym, ...baseFigs };
}

/** The reconcile "Off by": the projected book balance less the bank's newest printed balance, BOTH in
 * the account's own currency. null when there is no statement balance, or when only the base
 * projection of a foreign account is known (base − own would add two currencies). */
export function reconOffBy(fig, statementBalance){
  if(statementBalance === null || statementBalance === undefined || statementBalance === '') return null;
  if(!fig || !fig.own || !Number.isFinite(Number(statementBalance))) return null;
  return fig.projected - Number(statementBalance);
}

/** Whether the register offers to clear every gap warning on the account (owner 2026-10-28): only while it
 * reconciles — its Off by (reconOffBy) is under half a cent, the "✓ Reconciles" rule — and a line carries one. The
 * API checks it again before it clears anything. */
export function canClearGaps(offBy, gapCount){
  if(offBy === null || offBy === undefined || !Number.isFinite(Number(offBy))) return false;
  return Math.abs(Number(offBy)) < 0.005 && Number(gapCount) > 0;
}

/** The currency a DOCUMENT row is in → { code, symbol }, or null when it cannot be told. The row's own
 * currency_code / currency_symbol first (the API may send them), then its currency_id looked up in
 * `byId` (a Map id → { code, symbol }, see shared/currencies.js); a row with no currency is in the
 * company base → `base`, or { code: '', symbol: '' } (which fmtMoney marks with the base symbol). */
export function docCurrency(row, byId, base){
  const r = row || {};
  if(r.currency_symbol || r.currency_code) return { code: r.currency_code || '', symbol: r.currency_symbol || '' };
  const hasId = r.currency_id !== null && r.currency_id !== undefined && r.currency_id !== '';
  if(!hasId) return base ? { code: base.code || '', symbol: base.symbol || '' } : { code: '', symbol: '' };
  const c = byId && byId.get(String(r.currency_id));
  if(c) return { code: c.code || '', symbol: c.symbol || '' };
  if(base && base.id !== null && base.id !== undefined && String(base.id) === String(r.currency_id))
    return { code: base.code || '', symbol: base.symbol || '' };
  return null;
}

/** A figure in a known currency with that currency's mark; the bare figure when the currency is not
 * known — never another currency's mark. */
export function fmtIn(value, cur, signed){
  if(!cur) return fmtAmountOnly(value, signed);
  return fmtMoney(value, cur.symbol || cur.code, signed);
}
